import { cutToast } from 'utility/toast';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, Stack, TooltipHost } from '@fluentui/react';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Text, Icon } from '@fluentui/react';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import {
  EntityDeleteInput,
  EntityDocumentFilter,
} from 'common/types/globalTypes';
import { TABLE_ROWS } from 'common/constants';
import { fileType, fileTypeColor } from 'common/utils/fileType';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import { toReportOrderByVariable } from './reportUtils';
import {
  PersoanlReports,
  PersoanlReportsVariables,
  PersoanlReports_entityDocumentPersonalPools_nodes,
} from './__generated__/PersoanlReports';
import { useStyles } from './index.styles';

import { reportsColumns } from './columns.data';
import { ColumnData } from 'common/components/SearchBar';
import { getGlobalDateFormat } from 'common/utils/dateFormats';
import clsx from 'clsx';
import { InfiniteList } from 'common/components/InfiniteList';
import {
  EntityDocumentDelete,
  EntityDocumentDeleteVariables,
} from 'common/graphql/__generated__/EntityDocumentDelete';

import REPORTSLIST from './PersoanlReports.graphql';
import DELETE_DOCUMENTS from 'common/graphql/EntityDocumentDelete.graphql';
import { HighLightActiveLink } from 'common/components/HighLight';
import { useBoolean } from '@fluentui/react-hooks';
import { ReportView } from '../ReportView';
import { filterOptionProps } from 'documents/documentAssignment/folder/list/GroupHeader';
import {
  toFilterOrVariable,
  toFilterVariable,
  toOrderByVariable,
} from 'documents/personalPool/MyDocumentsList/utils';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { DocumentViewModal } from 'common/components/DocumentList/DocumentViewModal';

type ReportListItemType = PersoanlReports_entityDocumentPersonalPools_nodes;

type DocumentProps = PersoanlReports_entityDocumentPersonalPools_nodes;

interface ReportListProps {
  selectedItems: (dataList: DocumentProps[]) => void;
  callDeleteFunction?: boolean;
  resetFunctionCall: (value: boolean) => void;
  filterOptions: filterOptionProps;
}
export let listVariables: PersoanlReportsVariables | undefined;

export const ReportList: React.FC<ReportListProps> = ({
  selectedItems,
  callDeleteFunction,
  resetFunctionCall,
  filterOptions,
}) => {
  const styles = useStyles();
  const [selectedDocument, setSelectedDocument] =
    useState<ReportListItemType | null>(null);
  const [
    detailDialogVisible,
    { setTrue: showDetailDialog, setFalse: closeDetailDialog },
  ] = useBoolean(false);

  const [gridColumns, setGridColumns] = useState<ColumnData[]>(reportsColumns);
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>({
    column: 'date',
    direction: 'desc' as OrderDirection,
  });
  const [selectedList, setselectedList] = useState<DocumentProps[]>([]);
  const [docViewState, setDocViewState] = useState<DocumentViewModalState>({
    isOpen: false,
    _fileType: 'pdf',
  });
  const {
    loading: personalReportLoading,
    data: personalReportListData,
    error: personalReportListDataError,
    fetchMore,
    refetch,
    variables,
    networkStatus,
  } = useQuery<PersoanlReports, PersoanlReportsVariables>(REPORTSLIST, {
    variables: {
      first: TABLE_ROWS,
      orderBy: ['_CREATED_DATE_DESC', 'PRIMARY_KEY_ASC'],
      filter: {
        _isReportingDocument: { equalTo: true },
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const handleSortAndPagination = (
    sortOrder?: SortOrder,
    showMore?: boolean
  ) => {
    const variables: PersoanlReportsVariables = {
      first: TABLE_ROWS,
      orderBy: toReportOrderByVariable(sortOrder),
      filter: {
        _isReportingDocument: { equalTo: true },
      },
      after: showMore
        ? personalReportListData?.entityDocumentPersonalPools?.pageInfo
            ?.endCursor
        : null,
    };
    if (showMore) fetchMore({ variables });
    else refetch(variables);
  };
  const refetching =
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables ||
    networkStatus === NetworkStatus.loading;

  const sortColumns = (columnName: string, direction: string) => {
    let orderBy = {
      column: columnName,
      direction: direction as OrderDirection,
    };
    setSortOrderParam(orderBy);
    handleSortAndPagination(orderBy);
  };

  const _onColumnClick = (column: ColumnData) => {
    const newColumns: ColumnData[] = gridColumns.slice();
    const currColumn: ColumnData = newColumns.filter(
      (currCol) => column.key === currCol.key
    )[0];
    newColumns.forEach((newCol: ColumnData) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
      }
    });
    setGridColumns(newColumns);
    let direction = 'asc';
    if (column.isSortedDescending === true) {
      direction = 'asc';
      sortColumns(column.key, direction);
    } else {
      direction = 'desc';
      sortColumns(column.key, direction);
    }
  };

  const [deleteDocuments] = useMutation<
    EntityDocumentDelete,
    EntityDocumentDeleteVariables
  >(DELETE_DOCUMENTS, { errorPolicy: 'all' });

  const _renderItemColumn = (
    item: DocumentProps | undefined,
    _index: number | undefined,
    column: IColumn | undefined
  ) => {
    if (item) {
      switch (column?.key) {
        case 'documentTypes':
          return (
            <HighLightActiveLink
              preventDefault
              highlightText={''}
              text={item.documentTypes?.documentType ?? ''}
              isSelectedDocument={selectedDocument?.id === item.id}
              onLinkClick={async () => {
                const { data } = await refetch();
                const updatedItem =
                  data?.entityDocumentPersonalPools?.nodes.find(
                    (doc) => doc.id === item.id
                  );
                setSelectedDocument(updatedItem ?? item);
                showDetailDialog();
              }}
            />
          );
        case '_documentContents':
          return (
            <Stack
              horizontal
              tokens={{ childrenGap: 15 }}
              className={styles.onrenderColumnStack}
            >
              {item._documentContents && (
                <Text variant="medium">{item._documentContents}</Text>
              )}
            </Stack>
          );
        case 'comment':
          return (
            <Stack
              horizontal
              tokens={{ childrenGap: 15 }}
              className={styles.onrenderColumnStack}
            >
              {item.comment && <Text variant="medium">{item.comment}</Text>}
            </Stack>
          );
        case 'view':
          const viewDocumentDisable =
            (item._isProtected !== undefined ||
              item._fileViewer !== undefined) &&
            (item._isProtected || item._fileViewer !== 'browser');
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentDisable}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    setDocViewState({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        case 'fileReference':
          return (
            <Stack
              horizontal
              tokens={{ childrenGap: 15 }}
              className={styles.onrenderColumnStack}
            >
              <Icon
                iconName={fileType(item.iconType || 'OTHER')}
                className={fileTypeColor(item.iconType || 'OTHER')}
              />
              <Text variant="medium">{item.fileReference}</Text>
            </Stack>
          );
        case '_createdDate':
          return (
            <Stack tokens={{ childrenGap: 15 }} verticalAlign="center">
              <Text
                variant="medium"
                className={clsx(
                  styles.contentColumnAlignRight,
                  styles.paddinRight25
                )}
              >
                {getGlobalDateFormat(item._createdDate || '')}
              </Text>
            </Stack>
          );
        case '_sharedByUser':
          return (
            <Stack
              horizontal
              horizontalAlign="space-between"
              tokens={{ childrenGap: 15 }}
              className={styles.onrenderColumnStack}
            >
              {item._sharedByUser && (
                <Text variant="medium">{item._sharedByUser}</Text>
              )}
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 10 }}
              ></Stack>
            </Stack>
          );
      }
    }
  };

  const handleDelete = async () => {
    const selectionArray: DocumentProps[] = selectedList;
    const selectedData = selectionArray;
    const toDelete: DocumentProps[] = selectedData.map((_, index) => {
      return selectedData![index];
    });
    let deletableDocs = toDelete?.filter((item) => item._isDeletable !== false);
    if (deletableDocs.length > 0) {
      const deleteInputArray: EntityDeleteInput[] = deletableDocs.map(
        (item) => {
          return { id: item.id, rowTimestamp: item._rowTimestamp! };
        }
      );
      const { errors } = await deleteDocuments({
        variables: { input: { entityDelete: deleteInputArray } },
        optimisticResponse: {
          entityDocumentDelete: {
            deletedEntities: deleteInputArray,
          },
        },
        update: (cache, { data }) => {
          const deletedIds = data?.entityDocumentDelete?.deletedEntities?.map(
            (entity) => entity?.id
          );
          if (deletedIds) {
            const filteredList =
              personalReportListData?.entityDocumentPersonalPools?.nodes.filter(
                (emp) => deletedIds.indexOf(emp.id) === -1
              );
            const newData: PersoanlReports = {
              entityDocumentPersonalPools: {
                nodes: filteredList!,
                pageInfo:
                  personalReportListData?.entityDocumentPersonalPools
                    ?.pageInfo!,
                totalCount:
                  personalReportListData?.entityDocumentPersonalPools
                    ?.totalCount! - deletedIds.length,
              },
            };
            cache.writeQuery<PersoanlReports, PersoanlReportsVariables>({
              query: REPORTSLIST,
              variables,
              data: newData,
            });
          }
        },
      });

      if (errors?.length) cutToast.error(errors[0].message);
      else {
        resetFunctionCall(false);
        cutToast.success('Record deleted Successfully');
      }
    }
  };
  const handleDeleteMemo = useCallback(handleDelete, [callDeleteFunction]);

  useEffect(() => {
    if (callDeleteFunction) {
      handleDeleteMemo();
    }
  }, [callDeleteFunction, handleDeleteMemo]);

  const { filterTypes, startsWith } = { ...filterOptions };

  const handleSearch = async (showMore?: boolean) => {
    const searchOption = startsWith
      ? 'startsWithInsensitive'
      : 'includesInsensitive';

    const filtersApplied: EntityDocumentFilter | undefined = filterTypes?.length
      ? ({
          and: toFilterVariable(filterTypes, searchOption),
          or: toFilterOrVariable(filterTypes),
        } as EntityDocumentFilter)
      : undefined;
    const variables: PersoanlReportsVariables = {
      first: TABLE_ROWS,
      orderBy: toOrderByVariable(sortOrderParam),
      filter: {
        ...filtersApplied,
        _isReportingDocument: { equalTo: true },
      },
      after: showMore
        ? personalReportListData?.entityDocumentPersonalPools?.pageInfo
            ?.endCursor
        : undefined,
    };
    if (showMore) fetchMore({ variables });
    else refetch(variables);
  };

  const handleSearchMemo = useCallback(handleSearch, [
    filterTypes,
    sortOrderParam,
    startsWith,
  ]);

  const renderRef = useRef(false);

  useEffect(() => {
    if (renderRef.current) handleSearchMemo(false);
  }, [filterTypes, sortOrderParam, handleSearchMemo]);

  useEffect(() => {
    if (!renderRef.current) renderRef.current = true;
    else {
      handleSearchMemo(false);
    }
  }, [startsWith, handleSearchMemo]);

  return (
    <>
      <InfiniteList
        loading={
          (!personalReportListData && !personalReportListDataError) ||
          personalReportLoading
        }
        items={
          !refetching
            ? personalReportListData?.entityDocumentPersonalPools?.nodes || []
            : undefined
        }
        hasNextPage={
          personalReportListData?.entityDocumentPersonalPools?.pageInfo
            .hasNextPage
        }
        columns={gridColumns.filter((_column) => _column.isVisible)}
        onRenderItemColumn={_renderItemColumn}
        onColumnHeaderClick={(_, column) => {
          if (column) _onColumnClick(column);
        }}
        onLoadMore={() => handleSortAndPagination(sortOrderParam, true)}
        onSelectionChanged={(arr) => {
          setselectedList(arr);
          selectedItems(arr);
        }}
      />
      <Stack style={{ alignSelf: 'center' }}>
        <DocumentViewModal
          centerAlign
          onDismiss={() => setDocViewState({ isOpen: false, _fileType: 'pdf' })}
          {...docViewState}
        />
      </Stack>

      {detailDialogVisible && selectedDocument && (
        <ReportView
          onDismiss={closeDetailDialog}
          documentData={selectedDocument}
        />
      )}
    </>
  );
};
